import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { getApiUrl } from '@/config/api';

/**
 * Hook for real-time notification delivery via Server-Sent Events (SSE).
 *
 * Connects to /api/notifications/stream using a short-lived token.
 * On receiving a notification, invalidates React Query caches so the
 * bell icon and notification list update instantly.
 *
 * Falls back gracefully: if SSE fails, polling continues working.
 */
export function useNotificationStream({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const enabledRef = useRef(enabled);
  const mountedRef = useRef(true);
  const MAX_RETRIES = 5;

  // Keep enabledRef in sync
  enabledRef.current = enabled;

  useEffect(() => {
    mountedRef.current = true;

    const cleanup = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };

    const scheduleRetry = () => {
      if (!mountedRef.current || !enabledRef.current) return;
      if (retryCountRef.current >= MAX_RETRIES) return;

      const delay = Math.min(2000 * Math.pow(2, retryCountRef.current), 32000);
      retryCountRef.current += 1;

      retryTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current && enabledRef.current) {
          connect();
        }
      }, delay);
    };

    const connect = async () => {
      cleanup();

      if (!mountedRef.current || !enabledRef.current) return;

      try {
        const res = await apiClient.post(API_ENDPOINTS.NOTIFICATIONS_STREAM_TOKEN);
        const streamToken = res.data?.token;
        if (!streamToken || !mountedRef.current) return;

        const apiUrl = getApiUrl();
        const streamUrl = `${apiUrl}${API_ENDPOINTS.NOTIFICATIONS_STREAM}?token=${encodeURIComponent(streamToken)}`;

        const es = new EventSource(streamUrl);
        eventSourceRef.current = es;

        es.addEventListener('connected', () => {
          retryCountRef.current = 0;
        });

        es.addEventListener('notification', () => {
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        });

        es.onerror = () => {
          // EventSource auto-reconnect reuses the same URL (same stream token).
          // Our token has a 5 min TTL, so after the first drop the reuse will 401.
          // Force-close and go through scheduleRetry() → connect() to mint a fresh token.
          es.close();
          eventSourceRef.current = null;
          scheduleRetry();
        };
      } catch {
        scheduleRetry();
      }
    };

    // Close EventSource before browser unload to avoid Firefox "interrupted" warnings
    const handleBeforeUnload = () => cleanup();
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Small delay to survive React StrictMode double-mount in dev
    const initTimeout = enabled
      ? setTimeout(() => { if (mountedRef.current) connect(); }, 1500)
      : null;

    return () => {
      mountedRef.current = false;
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (initTimeout) clearTimeout(initTimeout);
      cleanup();
    };
  }, [enabled, queryClient]);

  return {};
}
